import type { BalanceData, Enemy, LevelSim, SimEvent, SpawnEntry } from "@highmarch/domain";
import { Immunity, SimEventKind, immunityOf as simImmunityOf } from "@highmarch/domain";
import { LEVELS_PER_CHAPTER } from "@highmarch/app";
import { isWyrm } from "./wyrmApi";

/**
 * M36 rare spawns and immune units, as the presentation reads them (docs/RARES.md; the M36 contract §2, §3, §5): the
 * sim's API (domain sim/rares.ts, sim/immunity.ts — `Enemy.immunity`, `Immunity`, the events `EnemyImmune`,
 * `RareSpawned`, `RareKilled`, `RareEscaped`, `sim.rare` / `sim.rareChest`, `offer.rare`) plus the presentation's own
 * per-rare look (sparkle colours, the night's sky) in one place for the views.
 */

export { Immunity };

/** The four immunities, in glyph/label order. */
export type ImmunityKind = "physical" | "magic" | "fire" | "slow";
export const IMMUNITY_KINDS: readonly ImmunityKind[] = ["physical", "magic", "fire", "slow"];
export function immunityBit(k: ImmunityKind): number {
  return k === "physical" ? Immunity.Physical : k === "magic" ? Immunity.Magic : k === "fire" ? Immunity.Fire : Immunity.Slow;
}
/** The kinds set in a mask, in order. */
export function immunityKinds(mask: number): ImmunityKind[] { return IMMUNITY_KINDS.filter((k) => (mask & immunityBit(k)) !== 0); }
/** "Immune to magic", "Immune to fire and slows". */
export function immunityLine(mask: number): string {
  const words: Record<ImmunityKind, string> = { physical: "physical damage", magic: "magic", fire: "fire", slow: "slows" };
  const ks = immunityKinds(mask).map((k) => words[k]);
  return ks.length === 0 ? "" : `Immune to ${ks.length === 1 ? ks[0] : `${ks.slice(0, -1).join(", ")} and ${ks[ks.length - 1]}`}`;
}

/** An enemy's immunity mask (`Enemy.immunity`). */
export function immunityOf(e: Enemy): number { return simImmunityOf(e) | 0; }

/** A spawn entry's immunity (the wave preview reads the schedule): `SpawnEntry.immunity`, 0 when absent. */
export function entryImmunity(entry: SpawnEntry): number { return (entry.immunity ?? 0) | 0; }

// -----------------------------------------------------------------------------------------------------------------
// The six rares (contract table, names fixed by the coordinator)
// -----------------------------------------------------------------------------------------------------------------

export interface RareDef {
  /** Balance id `rare_c<chapter>`. */
  readonly id: string;
  /** 1–6. */
  readonly chapter: number;
  readonly name: string;
  /** The sparkle's two colours (sheen, glitter, motes, world sparkles, the bar). */
  readonly sparkA: number;
  readonly sparkB: number;
  /** The night sky's horizon tint for its look (rareLook.ts). */
  readonly sky: number;
  /** The shine's strength (sheen, glints, glow pulse; 1 = rareShiny's tuning) and the body's albedo scale, per creature. */
  readonly shine?: number;
  readonly albedo?: number;
  /** Its `_glow` slot (ember veins, crystal hearts…) × this, for a creature that must read against its own night. */
  readonly glow?: number;
  /** The body's emissive wash in `sparkA` (showpiece.ts RARE_WASH for a rare; the Starfall Wyrm keeps its own colours: 0). */
  readonly wash?: number;
}

export const RARES: readonly RareDef[] = [
  { id: "rare_c1", chapter: 1, name: "Gilded Stag", sparkA: 0xffe7a0, sparkB: 0xfff8e8, sky: 0x9a7cc8 },
  // v3 models (2026-10-27): the Tortoise's shell is coloured plates with dark seams, no longer a white dome that bloomed,
  // so its shine is up (0.45 -> 0.6) at full albedo; the Salamander's sail now glows on its own (glow 1.8 -> 1.2)
  { id: "rare_c2", chapter: 2, name: "Pearl Tortoise", sparkA: 0x9ff3ff, sparkB: 0xffd6f4, sky: 0x6f8fd0, shine: 0.6 },
  { id: "rare_c3", chapter: 3, name: "Cinder Salamander", sparkA: 0xffa03a, sparkB: 0xffe08a, sky: 0xb0708a, glow: 1.2 },
  { id: "rare_c4", chapter: 4, name: "Amethyst Golem", sparkA: 0xc58cff, sparkB: 0x9ff0ff, sky: 0x8a70d0 },
  // v3 follow-up (2026-10-28): the Ram's pale fleece and the Toad's jade read silvery / glossy at 0.38 under the full shine
  { id: "rare_c5", chapter: 5, name: "Starhorn Ram", sparkA: 0xbfd8ff, sparkB: 0xf2f6ff, sky: 0x5f78c8, shine: 0.6 },
  { id: "rare_c6", chapter: 6, name: "Jade Toad King", sparkA: 0x8cff9a, sparkB: 0xffe58a, sky: 0x5f9a9a, shine: 0.7 },
];

/** The chapter (1-based) a level is in. */
export function chapterOfLevel(level: number): number { return Math.floor((Math.max(1, level) - 1) / LEVELS_PER_CHAPTER) + 1; }

/** The rare for a balance id (`rare_c3`), or null. */
export function rareById(id: string): RareDef | null { return RARES.find((r) => r.id === id) ?? null; }
/** The rare of a chapter (1-based, clamped to the six). */
export function rareOfChapter(chapter: number): RareDef { return RARES[Math.max(0, Math.min(RARES.length - 1, chapter - 1))]; }

/** Whether an archetype is a rare (`rare: true` in enemies.json: a nonzero `rareChapter`). */
export function isRareArchetype(B: BalanceData, archetype: number): boolean { return (B.enemies[archetype]?.rareChapter ?? 0) > 0; }

/** What the presentation knows of the living rare (`sim.rare`) with its look. */
export interface RareNow { readonly enemyId: number; readonly archetype: number; readonly startedTick: number; readonly def: RareDef }

/** The rare def for an archetype: its own chapter's, else (never in play) the level's chapter's. */
function defFor(sim: LevelSim, archetype: number): RareDef {
  const ch = sim.balance.enemies[archetype]?.rareChapter ?? 0;
  return ch > 0 ? rareOfChapter(ch) : rareOfChapter(chapterOfLevel(sim.levelNumber));
}

/** The living rare, or null. */
export function rareNow(sim: LevelSim): RareNow | null {
  const r = sim.rare;
  return r ? { enemyId: r.enemyId, archetype: r.archetype, startedTick: r.startedTick, def: defFor(sim, r.archetype) } : null;
}

/** The rare's index (1–6) when `e` is a rare, else 0: the views draw it as one (shiny, rare bar, no boss dusk). */
export function rareIndexOf(sim: LevelSim, e: Enemy): number { return sim.balance.enemies[e.archetype]?.rareChapter ?? 0; }

/**
 * Whether enemy `id` is (or was, this tick) a rare — or the Starfall Wyrm (M40): both are drawn as bosses with a moment
 * of their own (level/rareMoment.ts, level/wyrmMoment.ts), so the boss dusk, bed, banner and fall pass them by.
 */
export function isRareEnemy(sim: LevelSim, id: number): boolean {
  const e = sim.enemySlots.find((x) => x.id === id);
  return !!e && (rareIndexOf(sim, e) > 0 || isWyrm(sim, e));
}

/** Whether the open offer is one of the rare rally's three (`offer.rare`). */
export function offerIsRare(sim: LevelSim): boolean { return sim.currentOffer?.rare === true; }

/** The chest the rare paid this level (`sim.rareChest`): its rarity, or null. */
export function rareChestRarity(sim: LevelSim): number | null { return sim.rareChest?.rarity ?? null; }

// -----------------------------------------------------------------------------------------------------------------
// The feed: what happened to the rare this tick, from the sim's events
// -----------------------------------------------------------------------------------------------------------------

export type RareSignal =
  | { readonly kind: "spawned"; readonly enemyId: number; readonly def: RareDef }
  | { readonly kind: "killed"; readonly enemyId: number; readonly def: RareDef; readonly chestRarity: number | null }
  | { readonly kind: "escaped"; readonly enemyId: number; readonly def: RareDef }
  | { readonly kind: "immune"; readonly enemyId: number; readonly towerId: number; readonly immunity: number };

/**
 * Reads the tick's events from `from` on and says what happened to the rare (and which hits an immunity shrugged off).
 * Payloads (domain sim/rares.ts, sim/immunity.ts): RareSpawned(a = enemy id, b = chapter, c = archetype),
 * RareKilled(a = enemy id, b = chest rarity, c = archetype), RareEscaped(a = enemy id, c = archetype),
 * EnemyImmune(a = enemy id, b = tower id, c = the immunity bit).
 */
export function readRareSignals(sim: LevelSim, from: number, out: RareSignal[] = []): RareSignal[] {
  const events: readonly SimEvent[] = sim.events.all();
  for (let i = from; i < events.length; i++) {
    const ev = events[i];
    switch (ev.kind) {
      case SimEventKind.RareSpawned: out.push({ kind: "spawned", enemyId: ev.a, def: rareOfChapter(ev.b) }); break;
      case SimEventKind.RareKilled: out.push({ kind: "killed", enemyId: ev.a, def: defFor(sim, ev.c), chestRarity: ev.b }); break;
      case SimEventKind.RareEscaped: out.push({ kind: "escaped", enemyId: ev.a, def: defFor(sim, ev.c) }); break;
      case SimEventKind.EnemyImmune: out.push({ kind: "immune", enemyId: ev.a, towerId: ev.b, immunity: ev.c }); break;
      default: break;
    }
  }
  return out;
}
